import React, { useCallback, useEffect, useReducer } from 'react'
import { useForm, FormProvider } from 'react-hook-form'
import cx from 'classnames'
import _ from 'lodash'

import Button from 'Design/components/Button'
import ArrowLeft from 'Icons/arrow-left.svg'
import { useGetAccountQuery, useUpdateOnboardingDataMutation } from 'Services/account'

import OnboardingHeader from './OnboardingHeader'
import OnboardingFooter from './OnboardingFooter'
import OnboardingStepOne from './OnboardingStepOne'
import OnboardingStepTwo from './OnboardingStepTwo'
import OnboardingSlider from './OnboardingSlider'
import OnboardingGetStarted from './OnboardingGetStarted'
import OnboardingQuestion from './OnboardingQuestion'
import onboardingReducer from './onboardingReducer'
import { OnboardingContext } from './OnboardingContext'
import { onboardingJson as onboardingProcessDataSrc, ACTION } from './onboarding-process.js'
import styles from './Onboarding.module.css'

const TRACK_OF_BY_INTENT = {
  SUPPORT: 'ticket',
  SALES: 'lead',
  'E-COMMERCE': 'order'
}

const COUNT_INITIAL_VALUE = onboardingProcessDataSrc.slider.min || 100

const getSteps = (data) => {
  const { reasonForUsingBot, mainGoals, count } = data
  const isExploreMyOptionsSelected = reasonForUsingBot && reasonForUsingBot.id === 4

  const steps = [...onboardingProcessDataSrc.steps].filter((item) => {
    const step = { ...item }

    if (step.stepName === 'step_three' && isExploreMyOptionsSelected) {
      return false
    }

    if (step.id === 1) {
      step.completed = !!reasonForUsingBot
    } else if (step.id === 2) {
      step.completed = !!(mainGoals && mainGoals.length)
    } else if (step.id === 3) {
      step.completed = !!count
    } else {
      step.completed = false
    }

    return step
  })

  return steps
}

const getCurrentStepId = (data) => {
  const { reasonForUsingBot, mainGoals } = data

  return reasonForUsingBot ? (mainGoals.length ? 3 : 2) : 1
}

const getDefaultValues = (metaData) => {
  const stepOneDataSrc = _.cloneDeep(onboardingProcessDataSrc.purposeOfOnboarding)
  const stepOneSelectedData = stepOneDataSrc.find((i) => i.intent === metaData?.reasonForUsingBot)
  const reasonForUsingBot = stepOneSelectedData
    ? {
        title: stepOneSelectedData.title,
        id: stepOneSelectedData.id,
        intent: stepOneSelectedData.intent,
        trackOf: TRACK_OF_BY_INTENT[stepOneSelectedData.intent?.toUpperCase()]
      }
    : null
  const mainGoals = metaData?.mainGoal ? [...metaData.mainGoal] : []
  const count = metaData?.expectedCount || COUNT_INITIAL_VALUE
  const isGetStarted = !!reasonForUsingBot
  const steps = getSteps({ reasonForUsingBot, mainGoals, count })
  const currentStepId = getCurrentStepId({ reasonForUsingBot, mainGoals })

  return {
    reasonForUsingBot,
    mainGoals,
    count,
    isGetStarted,
    currentStepId,
    steps
  }
}

const INITIAL_STATE = {
  purposeOfOnboarding: [],
  goals: []
}

const Onboarding = (props) => {
  // props
  const { onboardingSkipped } = props

  // reducer
  const [state, dispatch] = useReducer(onboardingReducer, INITIAL_STATE)

  const [updateOnboardingSettings] = useUpdateOnboardingDataMutation()
  const account = useGetAccountQuery()

  // forms
  const formMethods = useForm({
    mode: 'onSubmit',
    reValidateMode: 'onChange',
    defaultValues: getDefaultValues(account?.data?.settings?.meta_data)
  })
  const { watch, setValue } = formMethods

  const steps = watch('steps')
  const isGetStarted = watch('isGetStarted')
  const currentStepId = watch('currentStepId')
  const reasonForUsingBot = watch('reasonForUsingBot')
  const mainGoals = watch('mainGoals')
  const count = watch('count')

  const setSelectedPurpose = useCallback(() => {
    const purposeOfOnboarding = onboardingProcessDataSrc.purposeOfOnboarding.map((item) => {
      const purpose = { ...item }
      if (!!reasonForUsingBot && reasonForUsingBot.id === purpose.id) {
        purpose.selected = true
        const data = { reasonForUsingBot: { ...reasonForUsingBot }, mainGoals, count }
        const steps = getSteps(data)
        setValue('steps', steps)
      } else {
        purpose.selected = false
      }
      return purpose
    })

    // update state based on reasonForUsingBot value
    dispatch({ type: ACTION.UPDATE_PURPOSE_OF_ONBOARDING_LIST, payload: { purposeOfOnboarding } })
  }, [count, mainGoals, reasonForUsingBot, setValue])

  const setSelectedGoals = useCallback(() => {
    const goals = onboardingProcessDataSrc.goals.reduce((acc, item) => {
      const goal = { ...item }
      goal.selected = mainGoals.includes(goal.title)
      if (goal.goalTypeId === reasonForUsingBot?.id) {
        acc.push(goal)
      }
      return acc
    }, [])

    // update state based on mainGoals value
    dispatch({ type: ACTION.UPDATE_GOALS_LIST, payload: { goals } })
  }, [mainGoals, reasonForUsingBot])

  useEffect(() => {
    setSelectedPurpose()
    setSelectedGoals()
  }, [setSelectedGoals, setSelectedPurpose])

  const onClickDotStep = (step) => {
    if (step.id < currentStepId) {
      onPrevStep(step.id)
    }
    if (step.id > currentStepId) {
      onNextStep(step.id)
    }
  }

  const onPrevStep = (jumpTo = undefined) => {
    let stepId = 1
    if (jumpTo) {
      stepId = jumpTo
    } else {
      if (currentStepId <= 3 && currentStepId > 1) {
        stepId = currentStepId - 1
      }
    }
    // update current step id
    setValue('currentStepId', stepId)
  }

  const onSaveOnboardingData = async (isCompleted) => {
    const expectedCount = count > 0 ? count : null
    const payload = {
      onboarding_complete: isCompleted,
      meta_data: {
        reasonForUsingBot: reasonForUsingBot.intent,
        mainGoal: mainGoals,
        expectedCount: reasonForUsingBot.id !== 4 && isCompleted ? expectedCount : null
      }
    }

    try {
      await updateOnboardingSettings(payload).unwrap()
    } catch (e) {
      const error = e.data || {}
      if (error && error.errors) {
        console.error(error.errors[0])
        alert(error.errors[0])
      } else {
        console.error('Unable to complete onboarding process', e)
        alert('Unable to complete onboarding process')
      }
    }
  }

  const onNextStep = (jumpTo = undefined) => {
    let currentStep, stepId
    const stepList = [...steps]
    if (currentStepId !== stepList.length) {
      currentStep = stepList.find((s) => s.id === currentStepId)
      if (!jumpTo) {
        stepId = currentStepId < 3 ? currentStepId + 1 : currentStepId
      } else {
        stepId = jumpTo
      }
      currentStep.completed = true
      // update current step id
      setValue('currentStepId', jumpTo || stepId)
      if (stepId) {
        setValue('steps', stepList)
      }
      // save draft onboarding data
      onSaveOnboardingData(false)
      return true
    }

    // save final onboarding data
    onSaveOnboardingData(true)
  }

  const onGetStarted = () => {
    localStorage.setItem('get_started', true)
    setValue('isGetStarted', true)
  }

  const onSkipOnBoarding = () => {
    onboardingSkipped()
  }

  const handleCardSelect = (data, stepName) => {
    if (stepName === 'step_one') {
      setValue('reasonForUsingBot', {
        title: data.title,
        id: data.id,
        intent: data.intent,
        trackOf: TRACK_OF_BY_INTENT[data.intent?.toUpperCase()]
      })
      setValue('mainGoals', [])
      setValue('count', COUNT_INITIAL_VALUE)
    } else if (stepName === 'step_two') {
      let goals = mainGoals && mainGoals.length ? [...mainGoals] : []

      if (data.selected && !goals.includes(data.title)) {
        goals.push(data.title)
      } else {
        goals = goals.filter((i) => i !== data.title)
      }

      setValue('mainGoals', goals)
    }
  }

  const onChangeCount = (count) => {
    setValue('count', count)
    const list = [...steps]
    const currentStep = list.find((s) => s.id === currentStepId)
    currentStep.completed = true
    setValue('steps', list)
  }

  return (
    <OnboardingContext.Provider value={{ state, dispatch }}>
      <FormProvider {...formMethods}>
        <div className={styles.OnboardingProcessContainer}>
          <OnboardingHeader />
          {!isGetStarted && (
            <OnboardingGetStarted
              name={account.data.company_name}
              onSkipOnBoarding={() => onSkipOnBoarding()}
              onGetStarted={() => onGetStarted()}
            />
          )}
          {isGetStarted && (
            <div className={styles.OnboardingProcessContent}>
              <div className={styles.QuestionSection}>
                <div className={styles.BackNav}>
                  {currentStepId !== 1 && (
                    <Button className={`${styles.BtnBack}`} variant='primary' onClick={() => onPrevStep()}>
                      <ArrowLeft className={styles.ArrowLeftSvg} />
                    </Button>
                  )}
                </div>
                <OnboardingQuestion />
              </div>
              {currentStepId === 1 && <OnboardingStepOne onCardSelect={(e) => handleCardSelect(e, 'step_one')} />}
              {currentStepId === 2 && <OnboardingStepTwo onCardSelect={(e) => handleCardSelect(e, 'step_two')} />}
              {currentStepId === 3 && (
                <OnboardingSlider defaultTooltipOpen='true' isTooltipOpen='true' onChangeCount={onChangeCount} />
              )}
              <div
                className={cx(styles.ActionContainer)}
                style={currentStepId !== 1 ? { justifyContent: 'space-between' } : { justifyContent: 'space-between' }}
              >
                <label className={styles.SkipLabel} onClick={onSkipOnBoarding}>
                  Skip for now
                </label>
                <div className={styles.OnboardingProcessStepper}>
                  {steps.map((data, key) => {
                    return (
                      <a
                        key={key}
                        onClick={() => onClickDotStep(data)}
                        className={cx(styles.Stepper, {
                          [styles.Active]: data.id === currentStepId,
                          [styles.Completed]: data.completed
                        })}
                      />
                    )
                  })}
                </div>
                <Button
                  className={`${styles.BtnNext}`}
                  variant='primary'
                  onClick={() => onNextStep()}
                  disabled={
                    currentStepId === 1
                      ? !reasonForUsingBot
                      : currentStepId === 2
                      ? !mainGoals?.length
                      : currentStepId === 3
                      ? count <= 0
                      : false
                  }
                >
                  {steps.length === currentStepId ? 'Finish' : 'Continue'}
                </Button>
              </div>
            </div>
          )}
          <OnboardingFooter />
        </div>
      </FormProvider>
    </OnboardingContext.Provider>
  )
}

export default Onboarding
